import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import { User, UserRole } from "common/models/types";
import { ROLE_NAMES } from "common/constants/roles";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import Spinner from "components/atoms/Spinner";
import Tag from "components/atoms/Tag";
import { formatDate } from "utils/DateUtil";
import { Flex } from "components/atoms";

interface UserTableProps {
  users: User[];
  selectedUsers: string[];
  onSelectUser: (userId: string) => void;
  onSelectAll: (selected: boolean) => void;
  onViewUser: (user: User) => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  isSearching?: boolean;
}

const TableContainer = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const TableWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  border-radius: 8px 8px 0 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  position: relative;
`;

const Thead = styled.thead`
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr<{ $isSelected?: boolean }>`
  border-bottom: 1px solid #e5e7eb;
  background: ${(props) => (props.$isSelected ? "#f0f9ff" : "transparent")};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${(props) => (props.$isSelected ? "#e0f2fe" : "#f9fafb")};
    cursor: pointer;
  }
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: #374151;
  white-space: nowrap;

  &:nth-child(1) {
    width: 5%;
  } /* 체크박스 */
  &:nth-child(2) {
    width: 12%;
  } /* 이름 */
  &:nth-child(3) {
    width: 20%;
  } /* 이메일 */
  &:nth-child(4) {
    width: 15%;
  } /* 회사 */
  &:nth-child(5) {
    width: 13%;
  } /* 전화번호 */
  &:nth-child(6) {
    width: 8%;
  } /* 면허번호 */
  &:nth-child(7) {
    width: 15%;
  } /* 역할 */
  &:nth-child(8) {
    width: 12%;
  } /* 가입일 */
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:first-child {
    overflow: visible;
    text-overflow: unset;
  }
`;

const Checkbox = styled.input`
  cursor: pointer;
  width: 16px;
  height: 16px;
`;

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px;
  border-top: 1px solid #e5e7eb;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 16px;
  text-align: center;
`;

const RoleTag = styled(Tag)`
  display: inline-block;
`;

const UserTable: React.FC<UserTableProps> = ({
  users,
  selectedUsers,
  onSelectUser,
  onSelectAll,
  onViewUser,
  hasMore,
  isLoadingMore,
  onLoadMore,
  isSearching = false,
}) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastItemRef = useRef<HTMLTableRowElement>(null);

  // 무한 스크롤 설정
  useEffect(() => {
    if (!hasMore || !lastItemRef.current) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          onLoadMore();
        }
      },
      { threshold: 0.5 }
    );

    observerRef.current.observe(lastItemRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [users, hasMore, isLoadingMore, onLoadMore]);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSelectAll(e.target.checked);
  };

  const handleSelectUser = (userId: string) => {
    onSelectUser(userId);
  };

  const handleRowClick = (user: User) => {
    onViewUser(user);
  };

  const getRoleColor = (role: UserRole | string): string => {
    switch (role) {
      case UserRole.ADMIN:
        return "Gray";
      case UserRole.USER:
        return "Green";
      case UserRole.GYEONGGI_PHARMACIST:
      case "경기도약사회":
        return "Blue";
      case UserRole.SEOUL_PHARMACIST:
      case "서울시약사회":
        return "Purple";
      case UserRole.INCHEON_PHARMACIST:
      case "인천시약사회":
        return "Skyblue";
      default:
        // 서울시약사회 Separator 방식 체크 ("서울시약사회/구로구" 등)
        if (role.startsWith("서울시약사회/")) {
          return "Purple";
        }
        // 인천시약사회 Separator 방식 체크 ("인천시약사회/남동구" 등)
        if (role.startsWith("인천시약사회/")) {
          return "Skyblue";
        }
        return "Gray";
    }
  };

  const getRoleAbbreviation = (role: UserRole | string): string => {
    switch (role) {
      case UserRole.ADMIN:
        return "관리자";
      case UserRole.USER:
        return "사용자";
      case UserRole.GYEONGGI_PHARMACIST:
      case "경기도약사회":
        return "경기도";
      case UserRole.SEOUL_PHARMACIST:
      case "서울시약사회":
        return "서울시";
      case UserRole.INCHEON_PHARMACIST:
      case "인천시약사회":
        return "인천시";
      default:
        // 서울시약사회 Separator 방식 체크 ("서울시약사회/구로구" 등)
        if (role.startsWith("서울시약사회/")) {
          return "서울시";
        }
        // 인천시약사회 Separator 방식 체크 ("인천시약사회/남동구" 등)
        if (role.startsWith("인천시약사회/")) {
          return "인천시";
        }
        return role.slice(0, 3);
    }
  };

  if (users.length === 0) {
    // 검색 중 남은 페이지를 자동 로드하는 동안에는 "없음" 대신 로딩 상태를 보여준다.
    const isSearchLoading = isSearching && (isLoadingMore || hasMore);

    return (
      <TableContainer>
        <EmptyState>
          {isSearchLoading ? (
            <>
              <Spinner />
              <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[600]} style={{ marginTop: "12px" }}>
                검색 중입니다...
              </Text>
            </>
          ) : (
            <>
              <Text textType="H4" style={{ marginBottom: "8px" }}>
                {isSearching ? "검색 결과가 없습니다" : "사용자가 없습니다"}
              </Text>
              <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[600]}>
                {isSearching ? "다른 검색어로 시도해보세요." : "새 사용자를 추가해보세요."}
              </Text>
            </>
          )}
        </EmptyState>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <TableWrapper>
        <Table>
          <Thead>
            <tr>
              <Th>
                <Checkbox
                  type="checkbox"
                  checked={selectedUsers.length === users.length && users.length > 0}
                  onChange={handleSelectAll}
                />
              </Th>
              <Th>이름</Th>
              <Th>이메일</Th>
              <Th>회사</Th>
              <Th>전화번호</Th>
              <Th>면허번호</Th>
              <Th>역할</Th>
              <Th>가입일</Th>
            </tr>
          </Thead>
          <Tbody>
            {users.map((user, index) => {
              const isLastItem = index === users.length - 1;
              const isSelected = selectedUsers.includes(user.id);

              return (
                <Tr
                  key={user.id || `new-user-${index}`}
                  ref={isLastItem ? lastItemRef : undefined}
                  $isSelected={isSelected}
                  onClick={() => handleRowClick(user)}
                >
                  <Td onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleSelectUser(user.id)}
                    />
                  </Td>
                  <Td>
                    <Flex alignItems="center" gap="4px">
                      {user.name || "-"}
                      {user.ymydId > 0 && (
                        <span title={`약문약답 ID: ${user.ymydId}`}>
                          <Tag size="Small" variant="Tinted" color="Skyblue">
                            YMYD
                          </Tag>
                        </span>
                      )}
                    </Flex>
                  </Td>
                  <Td title={user.email}>{user.email || "-"}</Td>
                  <Td title={user.company}>{user.company || "-"}</Td>
                  <Td>{user.phone || "-"}</Td>
                  <Td title={user.license}>{user.license || "-"}</Td>
                  <Td>
                    {user.roles?.map((role) => (
                      <RoleTag
                        key={role}
                        color={getRoleColor(role)}
                        size="Small"
                        variant="Tinted"
                        title={ROLE_NAMES[role] || role}
                      >
                        {getRoleAbbreviation(role)}
                      </RoleTag>
                    ))}
                  </Td>
                  <Td>{user.createdAt ? formatDate(user.createdAt) : "-"}</Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableWrapper>

      {(isLoadingMore || (hasMore && !isLoadingMore)) && (
        <LoadMoreContainer>
          {isLoadingMore ? (
            <Spinner />
          ) : (
            <Button
              buttonType="button"
              color="Secondary"
              size="Small"
              type="Text"
              variant="Outlined"
              onClick={onLoadMore}
            >
              더 보기
            </Button>
          )}
        </LoadMoreContainer>
      )}
    </TableContainer>
  );
};

export default UserTable;
